// Triangle meshes for box-level pieces (any shape), in Source coordinates. Texture coordinates are
// projected from world space (one tile per 128 units) so neighbouring pieces line up.

import * as THREE from 'three';
import { outline, rampRise, wedgeAlongX, type Shape } from '../maps/boxlevel/shapes';

type V = [number, number, number];

/**
 * Geometry of a piece of size (sx, sy, sz). Vertices are relative to `centre` unless `bake` is
 * set, then they're in world space (for merging static pieces). UVs always use world positions.
 */
export function shapeGeometry(
  shape: Shape,
  size: readonly number[],
  centre: readonly number[],
  turn = 0,
  bake = false,
): THREE.BufferGeometry {
  const [sx, sy, sz] = size;
  const hx = sx / 2;
  const hy = sy / 2;
  const hz = sz / 2;
  const pos: number[] = [];
  const nor: number[] = [];
  const uv: number[] = [];
  const off = bake ? centre : [0, 0, 0];

  const tri = (a: V, b: V, c: V, want: V) => {
    // Flat normal; flip the winding if it faces away from the wanted side.
    const ux = b[0] - a[0];
    const uy = b[1] - a[1];
    const uz = b[2] - a[2];
    const vx = c[0] - a[0];
    const vy = c[1] - a[1];
    const vz = c[2] - a[2];
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const l = Math.hypot(nx, ny, nz);
    if (l < 1e-9) return;
    nx /= l;
    ny /= l;
    nz /= l;
    if (nx * want[0] + ny * want[1] + nz * want[2] < 0) {
      [b, c] = [c, b];
      nx = -nx;
      ny = -ny;
      nz = -nz;
    }
    const ax = Math.abs(nx);
    const ay = Math.abs(ny);
    const az = Math.abs(nz);
    for (const p of [a, b, c]) {
      const wx = p[0] + centre[0];
      const wy = p[1] + centre[1];
      const wz = p[2] + centre[2];
      pos.push(p[0] + off[0], p[1] + off[1], p[2] + off[2]);
      nor.push(nx, ny, nz);
      if (az >= ax && az >= ay) uv.push(wx / 128, wy / 128);
      else if (ax >= ay) uv.push(wy / 128, wz / 128);
      else uv.push(wx / 128, wz / 128);
    }
  };

  if (shape === 'wedge') {
    // Ridge along the length at the top, slopes down to the long bottom edges.
    const alongX = wedgeAlongX(turn);
    const L = alongX ? hx : hy;
    const W = alongX ? hy : hx;
    // Local (along, across, z) to piece coordinates.
    const p = (a: number, w: number, z: number): V => (alongX ? [a, w, z] : [w, a, z]);
    const dir = (a: number, w: number, z: number): V => (alongX ? [a, w, z] : [w, a, z]);
    tri(p(-L, -W, -hz), p(L, -W, -hz), p(L, W, -hz), dir(0, 0, -1));
    tri(p(-L, -W, -hz), p(L, W, -hz), p(-L, W, -hz), dir(0, 0, -1));
    for (const s of [1, -1]) {
      const out = dir(0, s * sz, W);
      tri(p(-L, 0, hz), p(L, 0, hz), p(L, s * W, -hz), out);
      tri(p(-L, 0, hz), p(L, s * W, -hz), p(-L, s * W, -hz), out);
    }
    for (const s of [1, -1]) tri(p(s * L, -W, -hz), p(s * L, W, -hz), p(s * L, 0, hz), dir(s, 0, 0));
  } else if (shape === 'ramp') {
    const [rx, ry] = rampRise(turn);
    // Corners of the footprint; the ones on the rise side get the full height.
    const corners: [number, number][] = [
      [-hx, -hy],
      [hx, -hy],
      [hx, hy],
      [-hx, hy],
    ];
    const high = (x: number, y: number) => x * rx + y * ry > 0;
    const top = (x: number, y: number): V => [x, y, high(x, y) ? hz : -hz];
    const bot = (x: number, y: number): V => [x, y, -hz];
    // Bottom
    tri(bot(-hx, -hy), bot(hx, -hy), bot(hx, hy), [0, 0, -1]);
    tri(bot(-hx, -hy), bot(hx, hy), bot(-hx, hy), [0, 0, -1]);
    // Slope
    const up: V = [-rx * sz, -ry * sz, rx !== 0 ? sx : sy];
    tri(top(-hx, -hy), top(hx, -hy), top(hx, hy), up);
    tri(top(-hx, -hy), top(hx, hy), top(-hx, hy), up);
    // Walls: each side of the footprint, as a quad from the bottom to the slope (degenerate on the low side).
    for (let i = 0; i < 4; i++) {
      const [ax, ay] = corners[i];
      const [bx, by] = corners[(i + 1) % 4];
      const out: V = [(ax + bx) / 2, (ay + by) / 2, 0];
      tri(bot(ax, ay), bot(bx, by), top(bx, by), out);
      tri(bot(ax, ay), top(bx, by), top(ax, ay), out);
    }
  } else {
    const pts = outline(shape, hx, hy, turn);
    const n = pts.length;
    for (let i = 1; i + 1 < n; i++) {
      tri(
        [pts[0][0], pts[0][1], hz],
        [pts[i][0], pts[i][1], hz],
        [pts[i + 1][0], pts[i + 1][1], hz],
        [0, 0, 1],
      );
      tri(
        [pts[0][0], pts[0][1], -hz],
        [pts[i][0], pts[i][1], -hz],
        [pts[i + 1][0], pts[i + 1][1], -hz],
        [0, 0, -1],
      );
    }
    for (let i = 0; i < n; i++) {
      const [ax, ay] = pts[i];
      const [bx, by] = pts[(i + 1) % n];
      const out: V = [by - ay, -(bx - ax), 0];
      tri([ax, ay, -hz], [bx, by, -hz], [bx, by, hz], out);
      tri([ax, ay, -hz], [bx, by, hz], [ax, ay, hz], out);
    }
  }

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  return g;
}
